import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import * as Icons from 'lucide-react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Avatar,
  Divider
} from '@mui/material';

// Sample CSV template data
const SAMPLE_CSV_DATA = [
  {
    name: 'John Smith',
    email: 'john.smith@company.com',
    phone: '+1 (555) 123-4567',
    company: 'Tech Corp',
    department: 'Engineering',
    designation: 'Senior Developer',
    employeeId: 'EMP-001',
    bloodGroup: 'A+',
    location: 'New York, NY'
  },
  {
    name: 'Sarah Johnson',
    email: 'sarah.johnson@company.com',
    phone: '+1 (555) 234-5678',
    company: 'Tech Corp',
    department: 'Design',
    designation: 'UI/UX Designer',
    employeeId: 'EMP-002',
    bloodGroup: 'B+',
    location: 'San Francisco, CA'
  },
  {
    name: 'Mike Wilson',
    email: 'mike.wilson@company.com',
    phone: '+1 (555) 345-6789',
    company: 'Tech Corp',
    department: 'Marketing',
    designation: 'Marketing Manager',
    employeeId: 'EMP-003',
    bloodGroup: 'O+',
    location: 'Chicago, IL'
  }
];

// Function to download sample CSV
const downloadSampleCSV = () => {
  const headers = Object.keys(SAMPLE_CSV_DATA[0]);
  const csvContent = [
    headers.join(','),
    ...SAMPLE_CSV_DATA.map(row => 
      headers.map(header => `"${row[header] || ''}"`).join(',')
    )
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', 'sample_id_card_template.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`upload-tabpanel-${index}`}
    aria-labelledby={`upload-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const convertImageToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const ImageBatchConverter = ({ onImagesConverted, convertedImages }) => {
  const [converting, setConverting] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setConverting(true);
    setProgress(0);
    
    const results = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const base64 = await convertImageToBase64(file);
        results.push({
          id: `img_${Date.now()}_${i}`,
          name: file.name,
          originalName: file.name.replace(/\.[^/.]+$/, ""), // Remove extension for matching
          size: file.size,
          base64,
          type: file.type
        });
      } catch (e) {
        console.error(`Error converting ${file.name}:`, e);
      }
      setProgress(((i + 1) / files.length) * 100);
    }
    
    onImagesConverted(results);
    setConverting(false);
    setProgress(0);
  };

  const handleRemoveImage = (imageId) => {
    const updated = convertedImages.filter(img => img.id !== imageId);
    onImagesConverted(updated);
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          component="label"
          startIcon={<Icons.Upload />}
          disabled={converting}
        >
          {converting ? 'Converting...' : 'Select Images'}
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={handleFiles}
          />
        </Button>
        
        {converting && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary">
              Converting images... {Math.round(progress)}%
            </Typography>
          </Box>
        )}
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Upload all photos at once. The system will try to auto-match based on filenames.
        </Typography>
      </Box>

      {convertedImages.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Converted Images ({convertedImages.length})
              </Typography>
              <Button
                size="small"
                color="secondary"
                onClick={() => onImagesConverted([])}
                startIcon={<Icons.Trash2 />}
              >
                Clear All
              </Button>
            </Box>
            
            <Grid container spacing={2}>
              {convertedImages.map((image) => (
                <Grid item xs={12} sm={6} md={4} key={image.id}>
                  <Card variant="outlined" sx={{ position: 'relative' }}>
                    <Box sx={{ position: 'relative' }}>
                      <img
                        src={image.base64}
                        alt={image.name}
                        style={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover'
                        }}
                      />
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'error.light', color: 'white' }
                        }}
                        onClick={() => handleRemoveImage(image.id)}
                      >
                        <Icons.X size={16} />
                      </IconButton>
                    </Box>
                    <Box sx={{ p: 1 }}>
                      <Typography variant="caption" noWrap title={image.name}>
                        {image.name}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {(image.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

const PhotoSelector = ({ user, convertedImages, selectedPhoto, onPhotoSelect }) => {
  // Auto-suggest photos based on name matching
  const getSuggestedPhotos = () => {
    if (!convertedImages.length) return [];
    
    const userName = user.name.toLowerCase().replace(/\s+/g, '');
    const userEmployeeId = user.employeeId.toLowerCase();
    
    return convertedImages.filter(img => {
      const imgName = img.originalName.toLowerCase().replace(/\s+/g, '');
      return imgName.includes(userName) || 
             imgName.includes(userEmployeeId) ||
             userName.includes(imgName);
    });
  };

  const suggestedPhotos = getSuggestedPhotos();

  return (
    <Box>
      <FormControl fullWidth size="small">
        <InputLabel>Select Photo</InputLabel>
        <Select
          value={selectedPhoto || ''}
          onChange={(e) => onPhotoSelect(user.id, e.target.value)}
          label="Select Photo"
        >
          <MenuItem value="">
            <em>No photo</em>
          </MenuItem>
          {convertedImages.map((image) => (
            <MenuItem key={image.id} value={image.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img
                  src={image.base64}
                  alt={image.name}
                  style={{
                    width: 24,
                    height: 24,
                    objectFit: 'cover',
                    borderRadius: '50%'
                  }}
                />
                <span>{image.name}</span>
                {suggestedPhotos.some(s => s.id === image.id) && (
                  <Chip size="small" label="Suggested" color="primary" variant="outlined" />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {suggestedPhotos.length > 0 && !selectedPhoto && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="primary">
            💡 Found {suggestedPhotos.length} suggested photo(s) based on name matching
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Enhanced CSV parsing with better error handling
const parseCSVData = (csvText) => {
  return new Promise((resolve, reject) => {
    console.log('Starting CSV parse. Raw text length:', csvText.length);
    console.log('First 200 characters:', csvText.substring(0, 200));
    
    Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false, // Keep everything as strings initially
      delimiter: '', // Auto-detect
      newline: '', // Auto-detect
      encoding: 'UTF-8',
      transformHeader: (header) => {
        // Clean and normalize headers
        return header.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
      },
      transform: (value) => {
        // Clean up cell values
        return typeof value === 'string' ? value.trim() : value;
      },
      complete: (results) => {
        console.log('Papa Parse Results:', results);
        console.log('Parsed data length:', results.data.length);
        console.log('Meta info:', results.meta);
        console.log('Errors:', results.errors);
        
        if (results.errors.length > 0) {
          console.warn('CSV parsing errors:', results.errors);
        }
        
        try {
          // Process and validate data
          const processedData = results.data
            .filter(row => {
              // Filter out completely empty rows
              const hasData = Object.values(row).some(value => 
                value && value.toString().trim() !== ''
              );
              return hasData;
            })
            .map((row, index) => {
              console.log(`Processing row ${index + 1}:`, row);
              
              // Map possible header variations to our standard fields
              const getFieldValue = (fieldNames) => {
                for (const fieldName of fieldNames) {
                  const cleanFieldName = fieldName.toLowerCase().replace(/[^a-z0-9]/g, '');
                  if (row[cleanFieldName]) {
                    return row[cleanFieldName].toString().trim();
                  }
                }
                return '';
              };
              
              return {
                id: index + 1,
                name: getFieldValue(['name', 'fullname', 'username', 'employeename']) || `User ${index + 1}`,
                email: getFieldValue(['email', 'emailaddress', 'mail']) || '',
                phone: getFieldValue(['phone', 'phonenumber', 'mobile', 'contact']) || '+1 (555) 123-4567',
                company: getFieldValue(['company', 'companyname', 'organization', 'org']) || 'Company Name',
                department: getFieldValue(['department', 'dept', 'division']) || 'General',
                designation: getFieldValue(['designation', 'title', 'position', 'role', 'jobtitle']) || 'Employee',
                employeeId: getFieldValue(['employeeid', 'empid', 'id', 'staffid']) || `EMP-${String(index + 1).padStart(4, '0')}`,
                bloodGroup: getFieldValue(['bloodgroup', 'blood', 'bloodtype']) || 'Not Specified',
                location: getFieldValue(['location', 'address', 'city', 'office']) || 'Office Location'
              };
            });
          
          console.log('Final processed data:', processedData);
          resolve(processedData);
          
        } catch (error) {
          console.error('Error processing CSV data:', error);
          reject(error);
        }
      },
      error: (error) => {
        console.error('Papa Parse error:', error);
        reject(error);
      }
    });
  });
};

const CSVUploadIDGenerator = ({ open, onClose, onDataReady }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [csvData, setCsvData] = useState([]);
  const [convertedImages, setConvertedImages] = useState([]);
  const [photoMappings, setPhotoMappings] = useState({});
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoMatchAttempted, setAutoMatchAttempted] = useState(false);
  const [rawCsvPreview, setRawCsvPreview] = useState('');

  // Auto-match photos when both CSV and images are available
  useEffect(() => {
    if (csvData.length > 0 && convertedImages.length > 0 && !autoMatchAttempted) {
      autoMatchPhotos();
      setAutoMatchAttempted(true);
    }
  }, [csvData, convertedImages, autoMatchAttempted]);

  const autoMatchPhotos = () => {
    const newMappings = {};
    let matchCount = 0;

    csvData.forEach(user => {
      const userName = user.name.toLowerCase().replace(/\s+/g, '');
      const userEmployeeId = user.employeeId.toLowerCase();
      
      const matchedImage = convertedImages.find(img => {
        const imgName = img.originalName.toLowerCase().replace(/\s+/g, '');
        return imgName.includes(userName) || 
               imgName.includes(userEmployeeId) ||
               userName.includes(imgName);
      });

      if (matchedImage) {
        newMappings[user.id] = matchedImage.id;
        matchCount++;
      }
    });

    setPhotoMappings(newMappings);
    
    if (matchCount > 0) {
      setErrors(prev => [...prev, `✅ Auto-matched ${matchCount} photos based on filename similarity`]);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setLoading(true);
    setErrors([]);
    setRawCsvPreview('');
    
    const file = acceptedFiles[0];
    console.log('File selected:', file.name, 'Size:', file.size);
    
    try {
      // Read file as text first
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file, 'UTF-8');
      });
      
      setRawCsvPreview(text.substring(0, 500)); // Preview first 500 chars
      
      // Parse the CSV
      const processedData = await parseCSVData(text);
      
      if (processedData.length === 0) {
        throw new Error('No valid data found in CSV file. Please check the format.');
      }
      
      setCsvData(processedData);
      setActiveTab(1); // Switch to photos tab
      setAutoMatchAttempted(false); // Reset for new CSV
      setErrors([`✅ Successfully loaded ${processedData.length} records from CSV`]);
      
    } catch (error) {
      console.error('CSV processing error:', error);
      setErrors([`Error processing CSV: ${error.message}`]);
    } finally {
      setLoading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
      'text/plain': ['.csv']
    },
    multiple: false
  });

  const handlePhotoSelect = (userId, imageId) => {
    setPhotoMappings(prev => ({
      ...prev,
      [userId]: imageId
    }));
  };

  const handleConfirmUpload = () => {
    // Merge CSV data with selected photos
    const finalData = csvData.map(user => {
      const selectedImageId = photoMappings[user.id];
      const selectedImage = convertedImages.find(img => img.id === selectedImageId);
      
      return {
        ...user,
        avatar: selectedImage ? selectedImage.base64 : null
      };
    });

    console.log('Final data being sent:', finalData);
    onDataReady(finalData);
    onClose();
  };

  const getPhotoStats = () => {
    const totalUsers = csvData.length;
    const usersWithPhotos = Object.keys(photoMappings).length;
    return { totalUsers, usersWithPhotos };
  };

  const stats = getPhotoStats();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Enhanced CSV Upload with Photo Mapping</Typography>
          {stats.totalUsers > 0 && (
            <Chip
              label={`${stats.usersWithPhotos}/${stats.totalUsers} photos mapped`}
              color={stats.usersWithPhotos === stats.totalUsers ? "success" : "warning"}
              variant="outlined"
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="1. Upload CSV" icon={<Icons.FileText />} />
          <Tab 
            label="2. Convert Photos" 
            icon={
              <Badge badgeContent={convertedImages.length} color="primary">
                <Icons.Camera />
              </Badge>
            } 
          />
          <Tab 
            label="3. Map Photos" 
            icon={<Icons.UserCheck />}
            disabled={csvData.length === 0}
          />
        </Tabs>

        {errors.length > 0 && (
          <Alert 
            severity={errors.some(e => e.startsWith('✅')) ? "success" : "error"} 
            sx={{ mt: 2 }}
            onClose={() => setErrors([])}
          >
            {errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </Alert>
        )}

        <TabPanel value={activeTab} index={0}>
          {/* Sample CSV Download Section */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              📄 CSV Template
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Download the sample CSV template to ensure your file has the correct format and headers.
            </Typography>
            <Button
              variant="outlined"
              onClick={downloadSampleCSV}
              startIcon={<Icons.Download />}
              sx={{ mr: 2 }}
            >
              Download Sample CSV
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Required columns: name, email, phone, company, department, designation, employeeId, bloodGroup, location
            </Typography>
          </Box>

          {/* CSV Upload Area */}
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: 'primary.main',
              borderRadius: 2,
              p: 4,
              bgcolor: isDragActive ? 'primary.50' : 'transparent',
              textAlign: 'center',
              cursor: 'pointer'
            }}
          >
            <input {...getInputProps()} />
            <Icons.Upload size={48} color="#1976d2" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              {isDragActive ? 'Drop your CSV file here' : 'Upload CSV File'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drag & drop your CSV file here, or click to select
            </Typography>
          </Box>

          {loading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                Processing CSV file...
              </Typography>
            </Box>
          )}

          {/* Raw CSV Preview */}
          {rawCsvPreview && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                File Preview (first 500 characters):
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" component="pre" sx={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                  {rawCsvPreview}...
                </Typography>
              </Paper>
            </Box>
          )}

          {/* CSV Success */}
          {csvData.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                ✅ CSV Loaded Successfully ({csvData.length} records)
              </Typography>
              
              {/* Show preview of parsed data */}
              <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Employee ID</TableCell>
                      <TableCell>Department</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvData.slice(0, 5).map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.employeeId}</TableCell>
                        <TableCell>{user.department}</TableCell>
                      </TableRow>
                    ))}
                    {csvData.length > 5 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="caption" color="text.secondary">
                            ... and {csvData.length - 5} more records
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Button
                variant="contained"
                onClick={() => setActiveTab(1)}
                endIcon={<Icons.ArrowRight />}
              >
                Next: Convert Photos
              </Button>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <ImageBatchConverter
            onImagesConverted={setConvertedImages}
            convertedImages={convertedImages}
          />
          
          {convertedImages.length > 0 && csvData.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={() => setActiveTab(2)}
                endIcon={<Icons.ArrowRight />}
              >
                Next: Map Photos to Users
              </Button>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Map Photos to Users
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select the appropriate photo for each user. Auto-suggestions are provided based on filename matching.
            </Typography>
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Employee ID</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Photo Selection</TableCell>
                  <TableCell>Preview</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {csvData.map((user) => {
                  const selectedImageId = photoMappings[user.id];
                  const selectedImage = convertedImages.find(img => img.id === selectedImageId);
                  
                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{user.employeeId}</TableCell>
                      <TableCell>{user.department}</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <PhotoSelector
                          user={user}
                          convertedImages={convertedImages}
                          selectedPhoto={selectedImageId}
                          onPhotoSelect={handlePhotoSelect}
                        />
                      </TableCell>
                      <TableCell>
                        {selectedImage ? (
                          <Avatar
                            src={selectedImage.base64}
                            alt={user.name}
                            sx={{ width: 40, height: 40 }}
                          />
                        ) : (
                          <Avatar sx={{ width: 40, height: 40, bgcolor: 'grey.300' }}>
                            <Icons.User size={20} />
                          </Avatar>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        
        {activeTab === 2 && csvData.length > 0 && (
          <Button
            variant="contained"
            onClick={handleConfirmUpload}
            disabled={loading}
            startIcon={<Icons.Check />}
          >
            Confirm & Generate ID Cards
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CSVUploadIDGenerator;